import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuthStore } from "../state/authStore"
import { useDeviceStore, generateUuid } from "../state/deviceStore"
import { useCatalogStore } from "../state/catalogStore"
import { useOrderStore } from "../state/orderStore"
import { getCatalog } from "../api/catalog"
import { submitOrder, getFiscalStatus } from "../api/orders"
import { logout } from "../api/auth"
import { ApiClientError } from "../api/client"
import CartPanel from "../components/CartPanel"

export default function OrderPage() {
    const navigate = useNavigate()
    const token = useAuthStore(s => s.token)!
    const ime = useAuthStore(s => s.ime)
    const prezime = useAuthStore(s => s.prezime)
    const companyName = useAuthStore(s => s.companyName)
    const deviceUuid = useDeviceStore(s => s.deviceUuid)!
    const deviceNaziv = useDeviceStore(s => s.deviceNaziv)
    const clearAuth = useAuthStore(s => s.clear)
    const { grupe, podgrupe, artikli, loaded, loading, setCatalog, setLoading } = useCatalogStore()
    const items = useOrderStore(s => s.items)
    const total = useOrderStore(s => s.total())
    const totalCount = useOrderStore(s => s.totalCount())
    const addItem = useOrderStore(s => s.addItem)
    const clearOrder = useOrderStore(s => s.clear)
    const [selectedGrupaId, setSelectedGrupaId] = useState<number | "all">("all")
    const [selectedPodgrupaId, setSelectedPodgrupaId] = useState<number | "all">("all")
    const [submitting, setSubmitting] = useState(false)
    const [submitMessage, setSubmitMessage] = useState<string | null>(null)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [cartOpen, setCartOpen] = useState(false)

    useEffect(() => {
        if (!loaded && !loading && token && deviceUuid) {
            setLoading(true)
            getCatalog(token, deviceUuid)
                .then(c => setCatalog(c.grupe, c.podgrupe, c.artikli))
                .catch(err => {
                    if (err instanceof ApiClientError && err.status === 401) {
                        clearAuth()
                        navigate("/login", { replace: true })
                    }
                })
        }
    }, [token, deviceUuid, loaded, loading])

    const handleLogout = async () => {
        if (token) await logout(token).catch(() => { })
        clearAuth()
        clearOrder()
        navigate("/login", { replace: true })
    }

    const handleSubmit = async () => {
        if (items.length === 0 || !token || !deviceUuid) return
        setSubmitting(true)
        setSubmitMessage(null)
        setSubmitError(null)
        const idempotencyKey = generateUuid()
        try {
            const result = await submitOrder(token, deviceUuid, idempotencyKey, {
                stavke: items.map(i => ({ sifra: i.sifra, kolicina: i.kolicina, napomena: i.napomena })),
                nacinPlacanja: 1
            })
            setSubmitMessage("Šaljem na fiskalnu kasu...")
            let attempts = 0
            const pollInterval = setInterval(async () => {
                attempts++
                try {
                    const status = await getFiscalStatus(token, deviceUuid, result.fiskalniRedId)
                    if (status.status === "FISKALIZOVANO") {
                        clearInterval(pollInterval)
                        setSubmitMessage(`Račun štampan ✓ (${status.fiskRn || status.brd || "-"})`)
                        clearOrder()
                        setCartOpen(false)
                        setTimeout(() => setSubmitMessage(null), 3000)
                        setSubmitting(false)
                    } else if (status.status === "GRESKA") {
                        clearInterval(pollInterval)
                        setSubmitError(`Greška: ${status.greska || "Fiskalizacija nije uspjela."}`)
                        setSubmitting(false)
                    } else if (attempts >= 30) {
                        clearInterval(pollInterval)
                        setSubmitError("Fiskalni printer ne odgovara.")
                        setSubmitting(false)
                    }
                } catch { }
            }, 1000)
        } catch (err) {
            const msg = err instanceof ApiClientError ? (err.detail || err.message) : "Greška."
            setSubmitError(msg)
            setSubmitting(false)
        }
    }

    const handleGrupaClick = (grupaId: number | "all") => {
        setSelectedGrupaId(grupaId)
        setSelectedPodgrupaId("all")
    }

    const filteredPodgrupe = selectedGrupaId === "all"
        ? podgrupe
        : podgrupe.filter(p => p.grupaId === selectedGrupaId)

    const filteredArtikli = (() => {
        if (selectedPodgrupaId !== "all") {
            return artikli.filter(a => a.podgrupaId === selectedPodgrupaId)
        }
        if (selectedGrupaId !== "all") {
            const podgrupaIdsZaGrupu = podgrupe.filter(p => p.grupaId === selectedGrupaId).map(p => p.id)
            return artikli.filter(a => a.podgrupaId !== undefined && a.podgrupaId !== null && podgrupaIdsZaGrupu.includes(a.podgrupaId))
        }
        return artikli
    })()

    const NoImagePlaceholder = () => (
        <svg viewBox="0 0 64 64" className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300" fill="currentColor">
            <path d="M52 14H12c-2.2 0-4 1.8-4 4v28c0 2.2 1.8 4 4 4h40c2.2 0 4-1.8 4-4V18c0-2.2-1.8-4-4-4zm0 32H12V18h40v28z" />
            <path d="M22 30c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm22-2L36 38l-4-4-10 10h28L44 28z" />
        </svg>
    )

    return (
        <div className="flex-1 flex flex-col h-full overflow-hidden">
            {/* Header */}
            <header
                className="px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between text-slate-800 shadow-soft flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #B9D1EA 0%, #D7E4F2 100%)" }}
            >
                <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm sm:text-lg truncate">{ime} {prezime}</div>
                    <div className="text-xs text-slate-600 truncate">{companyName} · {deviceNaziv}</div>
                </div>
                <div className="flex gap-1 sm:gap-2 flex-shrink-0 ml-2">
                    <button
                        onClick={() => navigate("/active")}
                        className="bg-white/70 hover:bg-white active:bg-white/90 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold border border-capActive shadow-soft transition-colors"
                    >
                        Aktivne
                    </button>
                    <button
                        onClick={handleLogout}
                        className="bg-white/70 hover:bg-white active:bg-white/90 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold border border-capActive shadow-soft transition-colors"
                    >
                        Odjava
                    </button>
                </div>
            </header>

            {/* Main content area */}
            <div className="flex-1 flex min-h-0">
                {/* Catalog (full width on mobile, partial on desktop) */}
                <div className="flex-1 flex flex-col bg-antique-light min-w-0">
                    {/* Grupe */}
                    <div className="bg-capActive border-b-2 border-capActive-dark px-2 sm:px-3 py-2 overflow-x-auto whitespace-nowrap shadow-soft flex-shrink-0">
                        <button
                            onClick={() => handleGrupaClick("all")}
                            className={`inline-block px-3 sm:px-5 py-1.5 sm:py-2 mr-2 rounded-full font-semibold text-xs sm:text-sm transition-all ${selectedGrupaId === "all"
                                    ? "bg-primary-700 text-white shadow-medium"
                                    : "bg-white text-slate-700 border border-capActive-dark hover:bg-capActive-light"
                                }`}
                        >
                            Sve grupe
                        </button>
                        {grupe.map(g => (
                            <button
                                key={g.id}
                                onClick={() => handleGrupaClick(g.id)}
                                className={`inline-block px-3 sm:px-5 py-1.5 sm:py-2 mr-2 rounded-full font-semibold text-xs sm:text-sm transition-all ${selectedGrupaId === g.id
                                        ? "bg-primary-700 text-white shadow-medium"
                                        : "bg-white text-slate-700 border border-capActive-dark hover:bg-capActive-light"
                                    }`}
                            >
                                {g.naziv}
                            </button>
                        ))}
                    </div>

                    {/* Podgrupe */}
                    {filteredPodgrupe.length > 0 && (
                        <div className="bg-capInactive-light border-b-2 border-capInactive px-2 sm:px-3 py-1.5 sm:py-2 overflow-x-auto whitespace-nowrap flex-shrink-0">
                            <button
                                onClick={() => setSelectedPodgrupaId("all")}
                                className={`inline-block px-3 sm:px-4 py-1 sm:py-1.5 mr-2 rounded-full font-medium text-xs transition-all ${selectedPodgrupaId === "all"
                                        ? "bg-primary-600 text-white shadow-soft"
                                        : "bg-white text-slate-600 border border-capActive hover:bg-capActive-light"
                                    }`}
                            >
                                Sve podgrupe
                            </button>
                            {filteredPodgrupe.map(p => (
                                <button
                                    key={p.id}
                                    onClick={() => setSelectedPodgrupaId(p.id)}
                                    className={`inline-block px-3 sm:px-4 py-1 sm:py-1.5 mr-2 rounded-full font-medium text-xs transition-all ${selectedPodgrupaId === p.id
                                            ? "bg-primary-600 text-white shadow-soft"
                                            : "bg-white text-slate-600 border border-capActive hover:bg-capActive-light"
                                        }`}
                                >
                                    {p.naziv}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Artikli */}
                    <div className="flex-1 overflow-y-auto p-2 sm:p-3 pb-20 lg:pb-3">
                        {loading && <div className="text-center text-slate-500 mt-12">Učitavam katalog...</div>}
                        {loaded && filteredArtikli.length === 0 && (
                            <div className="text-center text-slate-500 mt-12">Nema artikala u ovoj kategoriji.</div>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3">
                            {filteredArtikli.map(a => (
                                <button
                                    key={a.id}
                                    onClick={() => addItem(a)}
                                    className="bg-white border border-capActive rounded-xl p-2 sm:p-3 text-left hover:border-primary-500 hover:shadow-medium active:scale-95 shadow-soft transition-all"
                                >
                                    <div className="aspect-square bg-gradient-to-br from-capInactive-light to-antique rounded-lg mb-2 flex items-center justify-center">
                                        <NoImagePlaceholder />
                                    </div>
                                    <div className="font-semibold text-slate-800 text-xs sm:text-sm line-clamp-2 min-h-[2.5em] leading-tight">
                                        {a.nazivArtikla}
                                    </div>
                                    <div className="text-primary-700 font-bold text-sm sm:text-base mt-1">
                                        {a.cijena.toFixed(2)} <span className="text-xs text-slate-500">KM</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Side cart - only visible on lg+ (desktop/tablet landscape) */}
                <div className="hidden lg:block w-80 lg:w-96 flex-shrink-0">
                    <CartPanel onSubmit={handleSubmit} submitting={submitting} />
                </div>
            </div>

            {/* Mobile bottom bar (visible only on screens < lg) */}
            {items.length > 0 && (
                <button
                    onClick={() => setCartOpen(true)}
                    className="lg:hidden fixed bottom-0 left-0 right-0 bg-primary-600 hover:bg-primary-700 active:bg-primary-900 text-white px-4 py-3 shadow-medium flex items-center justify-between z-30 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <div className="bg-white/20 rounded-full px-3 py-1 font-bold text-sm">
                            {totalCount.toFixed(totalCount % 1 === 0 ? 0 : 2)}
                        </div>
                        <span className="font-semibold">Pregled narudžbe</span>
                    </div>
                    <div className="font-bold text-lg">
                        {total.toFixed(2)} <span className="text-sm opacity-90">KM</span>
                    </div>
                </button>
            )}

            {/* Mobile cart bottom sheet */}
            {cartOpen && (
                <div className="lg:hidden fixed inset-0 z-50 flex flex-col">
                    {/* Backdrop */}
                    <div
                        className="flex-1 bg-black/50 backdrop-blur-sm"
                        onClick={() => setCartOpen(false)}
                    />
                    {/* Sheet */}
                    <div className="bg-antique-light h-[85vh] rounded-t-2xl shadow-2xl flex flex-col animate-slide-up">
                        {/* Drag handle */}
                        <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
                            <div className="w-12 h-1.5 bg-slate-300 rounded-full"></div>
                        </div>
                        {/* Close button */}
                        <div className="px-4 pb-2 flex items-center justify-between flex-shrink-0">
                            <div className="font-bold text-lg text-slate-800">
                                Narudžba ({items.length})
                            </div>
                            <button
                                onClick={() => setCartOpen(false)}
                                className="bg-slate-200 hover:bg-slate-300 text-slate-700 w-9 h-9 rounded-full flex items-center justify-center text-xl font-bold"
                                aria-label="Zatvori"
                            >
                                ×
                            </button>
                        </div>
                        {/* CartPanel content */}
                        <div className="flex-1 min-h-0">
                            <CartPanel onSubmit={handleSubmit} submitting={submitting} />
                        </div>
                    </div>
                </div>
            )}

            {/* Toast za submit poruke */}
            {(submitMessage || submitError) && (
                <div className="fixed bottom-20 lg:bottom-4 left-1/2 -translate-x-1/2 max-w-md w-full px-4 z-50">
                    <div className={`rounded-xl p-4 shadow-medium font-medium ${submitError ? "bg-red-600 text-white" : "bg-green-600 text-white"}`}>
                        {submitError || submitMessage}
                    </div>
                </div>
            )}
        </div>
    )
}